import React, { useState, useEffect } from 'react';
import { Star, Plus } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ReviewForm, SuccessModal } from './ReviewForm';

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/reviews/?limit=6`);
      
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des avis:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSuccess = () => {
    setShowReviewForm(false);
    setShowSuccessModal(true);
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    fetchReviews(); // Recharger les avis (au cas où il y aurait de nouveaux approuvés)
  };

  if (loading) {
    return (
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-6 text-center">
          <p className="text-xl text-gray-600">Chargement des témoignages...</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-100/20 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <Star className="h-4 w-4 fill-amber-600" />
              Témoignages
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Avis de
              <span className="bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent"> Nos Clients</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              La satisfaction de nos clients est notre plus grande fierté. Découvrez leurs expériences.
            </p>
          </div>

          {/* Testimonials Grid */}
          {reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600 mb-6">Soyez le premier à laisser un avis !</p>
              <Button
                onClick={() => setShowReviewForm(true)}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-4 rounded-full font-semibold shadow-lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                Laisser un avis
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
                {reviews.map((review, index) => (
                  <Card 
                    key={review.id}
                    className="bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 animate-fade-in"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <CardContent className="pt-8 pb-8 px-6">
                      {/* Stars */}
                      <div className="flex gap-1 mb-4">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={`star-${review.id}-${i}`} className="h-5 w-5 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      
                      {/* Quote */}
                      <p className="text-gray-700 mb-6 leading-relaxed italic">
                        "{review.comment}"
                      </p>
                      
                      {/* Author */}
                      <div className="border-t pt-4">
                        <p className="font-bold text-gray-900">{review.name}</p>
                        {review.event_type && (
                          <p className="text-sm text-amber-600">{review.event_type}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Add Review Button */}
              <div className="text-center">
                <Button
                  onClick={() => setShowReviewForm(true)}
                  className="group bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-2"
                >
                  <Plus className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90" />
                  Laisser votre avis
                </Button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Review Form Modal */}
      {showReviewForm && (
        <ReviewForm
          onClose={() => setShowReviewForm(false)}
          onSuccess={handleReviewSuccess}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal onClose={handleCloseSuccess} />
      )}
    </>
  );
};

export default Testimonials;
